// Iter53 — Countdown ("Next 24h") aggregator page.
//
// Single GET → renders two sections (My tasks · Connected to me) plus a
// summary header. Each item shows a live countdown timer driven by a 30-second
// setInterval. Items are clickable and route to the underlying resource.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useStore } from "../store";
import { Clock, AlertCircle, Truck, PackageSearch, Wrench, Network, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const SEV_COLOR = { s1: "#E24B4A", s2: "#f3b14a", s3: "#4ed084", s4: "#9aa1b3" };

const KIND_META = {
  incident_sla: { icon: AlertCircle, label: "Incident SLA", color: "#9ec3ec" },
  service_request_due: { icon: Network, label: "Service request", color: "#c89adb" },
  clean_point_plus_one: { icon: PackageSearch, label: "Clean point DN+1", color: "#f3b14a" },
};

const fmtCountdown = (deadlineIso, nowMs) => {
  const t = new Date(deadlineIso).getTime() - nowMs;
  const abs = Math.abs(t);
  const sign = t < 0 ? "-" : "";
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  if (h >= 24) return `${sign}${Math.floor(h / 24)}d ${h % 24}h`;
  if (h >= 1) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
};

const urgencyColor = (deadlineIso, nowMs) => {
  const dt = new Date(deadlineIso).getTime() - nowMs;
  if (dt < 0) return "#E24B4A";                       // overdue
  if (dt < 30 * 60_000) return "#E24B4A";              // < 30 min
  if (dt < 2 * 3_600_000) return "#f3b14a";            // < 2 h
  return "#9aebc2";                                    // clear
};

export default function Countdown() {
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);
  const [data, setData] = useState(null);
  const [view, setView] = useState("chronological");  // chronological | today | tomorrow
  const [nowMs, setNowMs] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/countdown/next-24h?horizon_hours=48`);
      setData(r.data);
    } catch (e) {
      addToast({ type: "error", title: "Failed to load countdown", body: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const filterByView = (items) => {
    if (!data) return items;
    if (view === "chronological") return items;
    const todayEnd = new Date(data.today_end).getTime();
    const tomorrowEnd = new Date(data.tomorrow_end).getTime();
    return items.filter((i) => {
      const ts = new Date(i.deadline_at).getTime();
      if (view === "today") return ts <= todayEnd;
      if (view === "tomorrow") return ts > todayEnd && ts <= tomorrowEnd;
      return true;
    });
  };

  const myTasks = useMemo(() => filterByView(data?.my_tasks || []), [data, view]); // eslint-disable-line
  const connected = useMemo(() => filterByView(data?.connected || []), [data, view]); // eslint-disable-line

  return (
    <div data-testid="countdown-workspace">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 style={{
            fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800,
            fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)",
            letterSpacing: "-0.5px", lineHeight: 1.05,
          }} data-testid="view-title">
            Next 24h · Countdown
          </h1>
          <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
            Everything due in the next working day(s) — assigned to you or linked to your tenant
          </div>
        </div>
        <SummaryStrip summary={data?.summary} loading={loading} />
      </div>

      {/* View toggle */}
      <div className="flex gap-1.5 mb-5" data-testid="countdown-view-toggle">
        {[
          { v: "chronological", label: "All (chronological)" },
          { v: "today", label: `Today (${data?.summary?.today ?? 0})` },
          { v: "tomorrow", label: `Tomorrow (${data?.summary?.tomorrow ?? 0})` },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setView(opt.v)}
            className="px-3 py-1.5 rounded"
            style={{
              background: view === opt.v ? "rgba(24,95,165,0.16)" : "transparent",
              border: `1px solid ${view === opt.v ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
              color: view === opt.v ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
              ...mono(11, undefined, undefined, "0.5px"),
              cursor: "pointer",
            }}
            data-testid={`countdown-view-${opt.v}`}
            aria-pressed={view === opt.v}
          >{opt.label}</button>
        ))}
      </div>

      <Section
        title="§A · My tasks"
        subtitle="Items you own or were assigned to"
        items={myTasks}
        loading={loading}
        nowMs={nowMs}
        onSelect={(i) => navigate(i.link)}
        testId="countdown-my-tasks"
        emptyMessage="No personal deliverables in this window. 🌤"
      />
      <Section
        title="§B · Connected to me"
        subtitle="Items where your tenant is a linked party"
        items={connected}
        loading={loading}
        nowMs={nowMs}
        onSelect={(i) => navigate(i.link)}
        testId="countdown-connected"
        emptyMessage="No connected deliverables in this window."
      />
    </div>
  );
}

const SummaryStrip = ({ summary, loading }) => {
  if (loading || !summary) {
    return <div style={mono(11, "var(--text-dim, #9198b0)")} data-testid="countdown-summary">Loading…</div>;
  }
  return (
    <div className="flex flex-wrap gap-3" data-testid="countdown-summary">
      <Pill label="Today" value={summary.today} color="#9ec3ec" testId="countdown-summary-today" />
      <Pill label="Tomorrow" value={summary.tomorrow} color="#c89adb" testId="countdown-summary-tomorrow" />
      <Pill label="Overdue" value={summary.overdue} color={summary.overdue > 0 ? "#E24B4A" : "#9aa1b3"} testId="countdown-summary-overdue" />
    </div>
  );
};

const Pill = ({ label, value, color, testId }) => (
  <div
    className="rounded-md px-3 py-2 flex items-baseline gap-2"
    style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.12)" }}
    data-testid={testId}
  >
    <span style={mono(9, "var(--text-dim, #9198b0)", 600, "1.2px")} className="uppercase">{label}</span>
    <span style={mono(18, color, 700)}>{value}</span>
  </div>
);

const Section = ({ title, subtitle, items, loading, nowMs, onSelect, testId, emptyMessage }) => (
  <div className="mb-8" data-testid={testId}>
    <div className="mb-3">
      <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)", letterSpacing: "-0.2px" }}>
        {title}
      </h2>
      <div style={mono(10, "var(--text-dim, #9198b0)")}>{subtitle}</div>
    </div>
    {loading ? (
      <Empty text="Loading…" testId={`${testId}-loading`} />
    ) : items.length === 0 ? (
      <Empty text={emptyMessage} testId={`${testId}-empty`} />
    ) : (
      <div className="space-y-2" data-testid={`${testId}-list`}>
        {items.map((it) => (
          <ItemCard key={it.id} item={it} nowMs={nowMs} onSelect={() => onSelect(it)} />
        ))}
      </div>
    )}
  </div>
);

const ItemCard = ({ item, nowMs, onSelect }) => {
  const meta = KIND_META[item.kind] || { icon: Clock, label: item.kind, color: "#9aa1b3" };
  const Icon = meta.icon;
  const c = urgencyColor(item.deadline_at, nowMs);
  const cd = fmtCountdown(item.deadline_at, nowMs);
  const overdue = new Date(item.deadline_at).getTime() < nowMs;
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full text-left rounded-md p-3 flex items-center gap-3"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: `1px solid ${overdue ? "rgba(226,75,74,0.45)" : "rgba(255,255,255,0.10)"}`,
        cursor: "pointer",
      }}
      data-testid={`countdown-item-${item.id}`}
      data-overdue={overdue ? "true" : "false"}
    >
      <div
        className="rounded-md flex items-center justify-center"
        style={{
          width: 36, height: 36, flexShrink: 0,
          background: `${meta.color}1a`,
          border: `1px solid ${meta.color}55`,
          color: meta.color,
        }}
      >
        <Icon size={16} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13, color: "var(--text, #f0f2f8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {item.title}
          </span>
          {item.severity && (
            <span style={{ ...mono(9, SEV_COLOR[item.severity] || "#9aa1b3", 700, "0.6px"), textTransform: "uppercase", padding: "1px 5px", border: `1px solid ${SEV_COLOR[item.severity]}66`, borderRadius: 3 }}>
              {item.severity}
            </span>
          )}
        </div>
        <div style={mono(10, "var(--text-dim, #9198b0)")} className="truncate">
          <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
          {" · "}
          {item.ref_id}
          {item.incident_id && item.incident_id !== item.ref_id && ` · ${item.incident_id}`}
          {item.subtitle && ` · ${item.subtitle}`}
        </div>
      </div>
      <div className="text-right" style={{ flexShrink: 0 }}>
        <div style={mono(16, c, 700, "0.5px")} data-testid={`countdown-item-${item.id}-cd`}>
          {overdue ? `${cd} overdue` : `${cd} left`}
        </div>
        <div style={mono(9, "var(--text-dim, #9198b0)")}>
          {new Date(item.deadline_at).toLocaleString()}
        </div>
      </div>
      <ArrowRight size={14} style={{ color: "var(--text-dim, #9198b0)", flexShrink: 0 }} />
    </button>
  );
};

const Empty = ({ text, testId }) => (
  <div
    className="rounded-md p-6 text-center"
    style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", ...mono(11, "var(--text-dim, #9198b0)") }}
    data-testid={testId}
  >{text}</div>
);
